import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { KPI, Ring, SeverityBadge } from "@/components/Primitives";
import { Activity, AlertTriangle, Shield, Server, Users, Ticket, Repeat, FileSignature, HardDriveDownload, ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { LineChart, Line, ResponsiveContainer, AreaChart, Area } from "recharts";

const spark = () => Array.from({ length: 20 }, (_, i) => ({ v: 30 + Math.round(Math.random() * 70) }));

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [recurring, setRecurring] = useState([]);
  const [aiSummary, setAiSummary] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);

  useEffect(() => {
    api.get("/dashboard/executive")
      .then(r => setData(r.data))
      .catch(() => setData({ scores: {}, counters: {} }));

    api.get("/recurring-issues")
      .then(r => {
        const payload = r.data;
        const rows = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : Array.isArray(payload?.data)
              ? payload.data
              : [];
        setRecurring(rows.slice(0, 5));
      })
      .catch(() => setRecurring([]));
  }, []);

  const generateAI = async () => {
    setLoadingAI(true);
    try {
      const { data } = await api.post("/ai/exec-summary");
      setAiSummary(data.summary);
    } finally { setLoadingAI(false); }
  };

  if (!data) return <div className="text-zinc-500 text-sm">Loading command center…</div>;

  // Merge over defaults (not `?? default`) so a partial/empty object from the API
  // or an error fallback still has every field — otherwise counters:{} would leave
  // total_assets undefined and crash on .toLocaleString().
  const s = {
    operational: 0, compliance: 0, security: 0, asset: 0, business: 0,
    ...(data?.scores || {}),
  };

  const c = {
    clients: 0, total_assets: 0, online_assets: 0, open_tickets: 0,
    critical_tickets: 0, security_findings: 0, recurring_issues: 0,
    contracts_expiring_90d: 0, backups_failed: 0, compliance_findings: 0,
    ...(data?.counters || {}),
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Executive Command Center</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Operational health at a glance</h1>
        </div>
        <button onClick={generateAI} disabled={loadingAI} data-testid="ai-exec-summary-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md px-3 py-2 transition-colors">
          <Sparkles size={14} /> {loadingAI ? "Generating…" : "AI Executive Summary"}
        </button>
      </div>

      {/* Health Rings */}
      <div className="grid grid-cols-5 gap-4 bg-zinc-950 border border-zinc-900 rounded-md p-6" data-testid="health-rings">
        <div className="flex flex-col items-center gap-2"><Ring value={s.operational} label="Operational" /></div>
        <div className="flex flex-col items-center gap-2"><Ring value={s.compliance} label="Compliance" /></div>
        <div className="flex flex-col items-center gap-2"><Ring value={s.security} label="Security" /></div>
        <div className="flex flex-col items-center gap-2"><Ring value={s.asset} label="Asset" /></div>
        <div className="flex flex-col items-center gap-2"><Ring value={s.business} label="Business" /></div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI testid="kpi-clients" label="Clients" value={c.clients} sub="Active engagements" />
        <KPI testid="kpi-assets" label="Total Assets" value={c.total_assets.toLocaleString()} sub={`${c.online_assets} online`} tone="good" />
        <KPI testid="kpi-tickets" label="Open Tickets" value={c.open_tickets} sub={`${c.critical_tickets} critical`} tone={c.critical_tickets > 10 ? "bad" : "default"} />
        <KPI testid="kpi-sec-findings" label="Security Findings" value={c.security_findings} sub="Open" tone="warn" />
        <KPI testid="kpi-recurring" label="Recurring Issues" value={c.recurring_issues} sub="Detected" tone="warn" />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={FileSignature} label="Contracts expiring 90d" value={c.contracts_expiring_90d} link="/contracts" />
        <Stat icon={HardDriveDownload} label="Failed Backups" value={c.backups_failed} link="/backups" tone={c.backups_failed > 0 ? "bad" : "good"} />
        <Stat icon={Shield} label="Compliance findings" value={c.compliance_findings} link="/compliance" />
        <Stat icon={Activity} label="Asset uptime" value={`${Math.round((c.online_assets / Math.max(c.total_assets, 1)) * 100)}%`} link="/rmm" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recurring Issues feed */}
        <div className="lg:col-span-2 bg-zinc-950 border border-zinc-900 rounded-md">
          <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900">
            <div className="flex items-center gap-2"><Repeat size={14} className="text-amber-400" /><div className="font-display font-medium">Recurring Issues Detected</div></div>
            <Link to="/recurring" className="text-xs text-blue-400 inline-flex items-center gap-1" data-testid="dash-view-all-recurring">View all <ArrowUpRight size={12} /></Link>
          </div>
          <div className="divide-y divide-zinc-900">
            {recurring.length === 0 && <div className="px-5 py-8 text-sm text-zinc-500">No recurring issues detected.</div>}
            {recurring.map(r => (
              <div key={r.id} className="px-5 py-3 flex items-center gap-4 hover:bg-zinc-900/40">
                <SeverityBadge level={r.severity}>{r.severity}</SeverityBadge>
                <div className="flex-1">
                  <div className="text-sm">{r.category} · <span className="text-zinc-500 font-mono">{r.occurrences} occurrences</span></div>
                  <div className="text-[11px] text-zinc-500 font-mono">First: {r.first_seen?.slice(0,10)} → Last: {r.last_seen?.slice(0,10)}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-emerald-400 text-sm">${(r.estimated_cost_usd || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Est. cost</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* AI summary */}
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="flex items-center gap-2 mb-3"><Sparkles size={14} className="text-blue-400" /><div className="font-display font-medium">AI Executive Brief</div></div>
          {!aiSummary && <div className="text-sm text-zinc-500">Generate a CIO-grade summary of the current posture, risks and recommended actions.</div>}
          {aiSummary && <pre className="text-xs whitespace-pre-wrap text-zinc-300 leading-relaxed max-h-96 overflow-auto" data-testid="ai-summary-content">{aiSummary}</pre>}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, link, tone="default" }) {
  const toneCls = { good: "text-emerald-400", bad: "text-red-400", warn: "text-amber-400", default: "text-zinc-100" }[tone];
  return (
    <Link to={link} className="bg-zinc-950 border border-zinc-900 rounded-md p-4 hover:border-zinc-800 transition-colors block group">
      <div className="flex items-center justify-between">
        <Icon size={14} className="text-zinc-500" />
        <ArrowUpRight size={12} className="text-zinc-700 group-hover:text-zinc-400" />
      </div>
      <div className={`mt-3 font-mono text-2xl ${toneCls}`}>{value}</div>
      <div className="text-[11px] text-zinc-500 mt-0.5">{label}</div>
    </Link>
  );
}
